import { User } from "oidc-client-ts"
import logger  from "./Logger";
import { getCurrentAuthToken as getCurrentAuthTokenAmplify, signIn} from "./Amplify";
import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { CognitoUserEtc } from "Types";
import { use } from "matter";
import { isCapacitorNative } from "Utils/Constants";
import { handleAuthErrors } from "./Forms";
import CapacitorStorage from "./CapacitorStorage";
import { oidcCache, oidcManager } from "Contexts/AuthWrapperContext";

// Custom event dispatched when authentication is irreparably broken (e.g., invalid_grant)
// The AuthWrapperContext listens for this and triggers a full logout
export const AUTH_FORCE_LOGOUT_EVENT = 'museflow:force-logout';

export function dispatchForceLogout(reason: string) {
  logger.warn(`Force logout triggered: ${reason}`);
  window.dispatchEvent(new CustomEvent(AUTH_FORCE_LOGOUT_EVENT, { detail: { reason } }));
}
const cognitoUserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;

export async function manuallyClearUser() {
  logger.debug("manuallyClearUser called, clearing OIDC user storage");
  oidcCache.removeItem(`oidc.user:https://cognito-idp.us-west-2.amazonaws.com/${cognitoUserPoolId}:${cognitoClientId}`)
  logger.debug("OIDC user storage cleared");
}   

 const tryGetUser = async () => {
    let tryCount = 0;
    while( tryCount < 3) {
      try {
        const user = await getCurrentUser(); 
        return user;
      } catch (err) {
        if (tryCount === 2) {
          throw err;
        } else if (err instanceof Error && err.message === "Auth UserPool not configured.") {
          logger.error("Auth UserPool not configured, waiting 200 milliseconds before retrying");
          tryCount++;
          await new Promise(resolve => setTimeout(resolve, 200)); // wait 200 millisecond before retrying
        } else {
          throw err; // rethrow other errors
        }
      }
  }
}

// Cache clearing function - set by withCache, called on logout
let clearAuthCacheCallback: (() => void) | null = null;

function withCache<T>(
  fn: (clearCache?: boolean) => Promise<T>,
  cacheTimeMs: number = 2000
): (clearCache?: boolean) => Promise<T> {
  let cachedResult: T | undefined = undefined;
  let cachedTimestamp: number | undefined = undefined;

  // Expose cache clearing via module-level callback
  clearAuthCacheCallback = () => {
    cachedResult = undefined;
    cachedTimestamp = undefined;
  };

  return async (clearCache = false) => {
    const now = Date.now();
    if(clearCache) {
      cachedResult = undefined;
      cachedTimestamp = undefined;
    }
    
    if (cachedResult && cachedTimestamp && (now - cachedTimestamp < cacheTimeMs)) {
      return cachedResult;
    }

    const result = await fn(clearCache);
    cachedResult = result;
    cachedTimestamp = Date.now();
    return result;
  };
}

// Clear the auth cache - MUST be called on logout to prevent stale tokens
export function clearAuthCache() {
  if (clearAuthCacheCallback) {
    clearAuthCacheCallback();
    logger.debug("Auth cache cleared");
  }
}

async function getUserInternal(clearCache?: boolean): Promise<CognitoUserEtc | undefined> {
  let user: User | null = null;
  if(clearCache) {
    try {
      user = await oidcManager.signinSilent();
    } catch (error: any) {
      // Check for invalid_grant error or 401 - this means refresh token is expired/revoked
      const errorMessage = error?.message || error?.error || String(error);
      const status = error?.status || error?.response?.status;
      const isInvalidGrant = errorMessage.includes('invalid_grant') || 
          error?.error === 'invalid_grant' ||
          (error?.name === 'ErrorResponse' && errorMessage.includes('invalid_grant'));
      const is401 = status === 401 || errorMessage.includes('401');
      
      if (isInvalidGrant || is401) {
        logger.error(`Token refresh failed (invalid_grant: ${isInvalidGrant}, 401: ${is401}) - forcing logout`);
        dispatchForceLogout(`Token refresh failed: ${isInvalidGrant ? 'invalid_grant' : '401'}`);
        return undefined;
      }
      // Re-throw other errors
      throw error;
    }
  } else {
    user = await oidcManager.getUser();
  }
  if (user) {
    return {
        signInUserSession: {
            accessToken: {
                jwtToken: user.access_token || "",
            }
        },
        username: user.profile.email || "",
        attributes: {
            email: user.profile.email,
            sub: user.profile.sub,
            phone_number: user.profile.phone_number,
            name: user.profile.name,
        }
    };
  }
  try {
    const user = await tryGetUser();
    if(user) {
        const attributes = await fetchUserAttributes();
        const authToken = await getCurrentAuthTokenAmplify();
        const returnValue = {
            signInUserSession: {
                accessToken: {
                    jwtToken: authToken || "",
                }
            },
            username: attributes.email || "",
            attributes: {
                email: attributes.email,
                sub: attributes.sub,
                phone_number: attributes.phone_number,
                name: attributes.name,
            }
        };
        return returnValue;   
    }
  } catch (error) {
      logger.error("Error getting user:", error);
      handleAuthErrors(error as any);
      return undefined;
  }
  return undefined;
}

const jwtExpired = (jwt: string) => {
  if (!jwt) return false;
  try {
    const payload = jwt.split('.')[1];
    if (!payload) return true;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = JSON.parse(atob(padded));
    const exp = typeof decoded.exp === 'number' ? decoded.exp : 0;
    // allow small clock skew
    return exp * 1000 <= Date.now() + 30000;
  } catch {
    return true;
  }
}

// cognito doesn't keep token in local storage, so every request hits the API, which results
// in 400 errors on startup. Also, there isn't really a point in going to localstorage if we
// don't need to anyway. Cache is pull through for 15 minutes. If the token expires, getUserInternal
// will refresh it.
export const getUser = withCache(getUserInternal, 900000);

export async function getCurrentAuthToken(): Promise<string | undefined> {
    const user = await getUser();
    // Check JWT expiration and refresh user if needed
    try {
      const jwt = user?.signInUserSession?.accessToken?.jwtToken;
      const isExpired = jwtExpired(jwt || "");

      if (isExpired) {
        console.log("token is expired, refreshing user");
        const refreshedUser = await getUser(true)
        if (refreshedUser?.signInUserSession?.accessToken?.jwtToken) {
          return refreshedUser.signInUserSession.accessToken.jwtToken;
        }
        // If refresh returned undefined (e.g., after force logout), don't continue
        if (!refreshedUser) {
          logger.warn("Token refresh returned no user - session may have been invalidated");
          return undefined;
        }
      }
    } catch (e: any) {
      // Check for invalid_grant or 401 in the error
      const errorMessage = e?.message || e?.error || String(e);
      const status = e?.status || e?.response?.status;
      const isInvalidGrant = errorMessage.includes('invalid_grant') || e?.error === 'invalid_grant';
      const is401 = status === 401 || errorMessage.includes('401');
      
      if (isInvalidGrant || is401) {
        logger.error(`Token refresh failed during getCurrentAuthToken (invalid_grant: ${isInvalidGrant}, 401: ${is401})`);
        dispatchForceLogout(`Token refresh failed: ${isInvalidGrant ? 'invalid_grant' : '401'}`);
        return undefined;
      }
      logger.warn("JWT check/refresh failed", e as any);
    }
    logger.debug("getCurrentAuthToken called, user:", user);
    if (user && user.signInUserSession.accessToken.jwtToken) {
      logger.debug("getCurrentAuthToken called, returning oidc user access token:", user.signInUserSession.accessToken.jwtToken);
        return user.signInUserSession.accessToken.jwtToken;
    }
    const amplifyToken = await getCurrentAuthTokenAmplify();
    if( amplifyToken) {
      logger.debug("getCurrentAuthToken called, returning Amplify token:", amplifyToken);
      return amplifyToken;
    }
    logger.warn("No user or access token found in OIDC storage, returning null");
    return undefined;
}